import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { agencyApi } from '../services/api';

const TabPanel = ({ children, value, index }) => (
  <Box sx={{ display: value !== index ? 'none' : 'block', pt: 3 }}>
    {children}
  </Box>
);

const Profile = () => {
  const { agency, updateAgency } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    primaryColor: '#06AB8B',
    secondaryColor: '#049572',
  });

  useEffect(() => {
    if (agency) {
      setFormData({
        name: agency.name || '',
        phone: agency.phone || '',
        primaryColor: agency.primaryColor || '#06AB8B',
        secondaryColor: agency.secondaryColor || '#049572',
      });
    }
  }, [agency]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await agencyApi.updateProfile(formData);

      if (response.data.success) {
        updateAgency(response.data.agency);
        toast.success('Profile updated successfully!');
      } else {
        setError(response.data.message || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          Agency Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          Manage your agency profile and preferences
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {/* Agency Info Header */}
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              fontSize: 32,
            }}
          >
            {agency?.logoUrl ? (
              <img
                src={agency.logoUrl}
                alt={agency.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              agency?.name?.charAt(0) || 'A'
            )}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              {agency?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {agency?.email}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#06AB8B',
                bgcolor: '#06AB8B15',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                mt: 1,
              }}
            >
              {agency?.plan?.charAt(0).toUpperCase() + agency?.plan?.slice(1)} Plan
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ px: 3, borderBottom: '1px solid #eee' }}
        >
          <Tab label="General" />
          <Tab label="Branding" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* General Tab */}
            <TabPanel value={tab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Agency Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={agency?.email || ''}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={agency?.slug || ''}
                    disabled
                    helperText="URL slug cannot be changed"
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Branding Tab */}
            <TabPanel value={tab} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                    Primary Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      name="primaryColor"
                      value={formData.primaryColor}
                      onChange={handleChange}
                      style={{
                        width: 60,
                        height: 40,
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                      }}
                    />
                    <TextField
                      size="small"
                      name="primaryColor"
                      value={formData.primaryColor}
                      onChange={handleChange}
                      sx={{ width: 120 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                    Secondary Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      name="secondaryColor"
                      value={formData.secondaryColor}
                      onChange={handleChange}
                      style={{
                        width: 60,
                        height: 40,
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                      }}
                    />
                    <TextField
                      size="small"
                      name="secondaryColor"
                      value={formData.secondaryColor}
                      onChange={handleChange}
                      sx={{ width: 120 }}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Preview */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Preview
                </Typography>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${formData.primaryColor} 0%, ${formData.secondaryColor} 100%)`,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                    {formData.name || 'Your Agency'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Agency branding preview
                  </Typography>
                </Paper>
              </Box>
            </TabPanel>

            {/* Submit Button */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
